/**
 * DeArrow — replaces YouTube clickbait titles and thumbnails
 *
 * Declares a single web-accessible-resource group of ~19 files matched to <all_urls>
 * with no `use_dynamic_url`, so every one is fetchable from any page at a fixed URL.
 * The set includes vendor-named icons (`icons/logo-128.png`, `icons/logo.svg`) and the
 * popup bundle (`js/popup.js`, `popup.css`).
 *
 * The 128px logo is the cleanest signal: it is vendor-named, an image (so `probeImage`
 * reaches it with no CORS grant and survives a strict page CSP), and is served only when
 * the extension is installed.
 *
 * Manifest verified against version 2.3.10.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'enamippconapkdmgfgjchkhakpfinmaj';

export const dearrow: ExtensionDefinition = {
  id: ID,
  name: 'DeArrow - Better Titles and Thumbnails',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Nineteen static WAR on <all_urls> with no dynamic URL, including vendor-named ' +
    'logo icons. Detection is a single fetch/image load and conclusive.',
  signals: [
    {
      id: 'war-logo-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load icons/logo-128.png via <img> — a vendor-named icon exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icons/logo-128.png'),
    },
    {
      id: 'war-popup-js',
      kind: 'web-accessible-resource',
      description: 'Fetch js/popup.js — the popup bundle exposed to <all_urls>.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/popup.js'),
    },
    {
      id: 'war-popup-css',
      kind: 'web-accessible-resource',
      description: 'Fetch popup.css — a third independent path corroborating the match.',
      weight: 0.5,
      run: () => probeFetch(ID, 'popup.css'),
    },
  ],
};
